<template>
  <template v-if="$route.name === 'subjects.index'">
    <SubjectFilters 
      :filters="filters"
      :classes="classes"
      @update:filters="updateFilters"
      @apply-filters="fetchSubjects"
      @reset-filters="resetFilters"
      @add="navigateToAdd"
    />
    
    <SubjectTable
      :subjects="subjects"
      :loading="isLoading"
      @delete-subject="openDeleteDialog"
      @view-teachers="viewTeachers"
    />
    
    <PaginationControls
      v-if="pagination.totalItems > 0 && !isLoading"
      :pagination="pagination"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />
    
    <SubjectDeleteDialog
      ref="deleteDialog"
      @delete="deleteSubject"
    />
  </template>
  <router-view></router-view>
</template>
<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import SubjectTable from '@/components/features/subjects/SubjectTable.vue';
import SubjectFilters from '@/components/features/subjects/SubjectFilters.vue';
import SubjectDeleteDialog from '@/components/features/subjects/dialogs/SubjectDeleteDialog.vue';
import PaginationControls from '@/components/common/PaginationControls.vue';
import api from '@/api';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();
const router = useRouter();
const toast = useToast();
const store = useStore();
const deleteDialog = ref(null);
const isLoading = ref(false);

// State
const filters = ref({
  search: '',
  class_id: null
});
const classes = ref([]);

// Getters
const subjects = computed(() => store.getters['subjects/subjects']);
const pagination = computed(() => store.getters['subjects/pagination']);

/* ---------- helpers: consistent error toasts ---------- */
function extractApiMessage(error, fallbackKey = 'errors.generic') {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }
    if (typeof data.message === 'string') return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = 'errors.generic') {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t('auth.unauthorized', 'Unauthorized'));
  if (s === 403) return toast.error(t('auth.forbidden', 'Forbidden')); // <- explicit forbidden
  return toast.error(extractApiMessage(error, fallbackKey));
}

/* ---------- methods ---------- */
const updateFilters = (newFilters) => {
  filters.value = newFilters;
};

// Fetch subjects
const fetchSubjects = async () => {
  isLoading.value = true;
  try {
    await store.dispatch('subjects/fetchSubjects', {
      page: pagination.value.currentPage,
      per_page: pagination.value.perPage,
      search: filters.value.search,
      class_id: filters.value.class_id
    });
  } catch (error) {
    notifyError(error, 'subjects.fetchError');
    console.error("Error fetching subjects:", error);
  } finally {
    isLoading.value = false;
  }
};

// Reset filters
const resetFilters = () => {
  filters.value = {
    search: '',
    class_id: null
  };
  fetchSubjects();
};

// Pagination
const handlePageChange = () => fetchSubjects();
const handlePerPageChange = (perPage) => {
  store.dispatch('subjects/setPerPage', perPage);
  fetchSubjects();
};

// Delete
const openDeleteDialog = (subject) => deleteDialog.value?.open(subject);
const deleteSubject = async (id) => {
  isLoading.value = true;
  try {
    await store.dispatch('subjects/deleteSubject', id);
    toast.success(t('subjects.deleteSuccess'));
    await fetchSubjects();
  } catch (error) {
    if (error?.response?.status === 403) {
      toast.error(t('auth.forbidden', 'Forbidden')); // <- Forbidden on delete
    } else {
      toast.error(extractApiMessage(error, 'subjects.deleteFail'));
    }
    console.error("Error deleting subject:", error);
  } finally {
    isLoading.value = false;
  }
};

// Navigation
const navigateToAdd = () => router.push({ name: 'subjects.create' });
const viewTeachers = (subject) => {
  if (subject.class_id) {
    router.push({
      name: 'class-subject-teachers',
      params: {
        classId: subject.class_id,
        subjectId: subject.id
      }
    });
  } else {
    toast.warning(t('subjects.notAssigned'));
  }
};

// On mounted
onMounted(async () => {
  isLoading.value = true;
  try {
    const response = await api.get("/classes");
    classes.value = response.data?.data ?? [];
    await fetchSubjects();
  } catch (error) {
    if (error?.response?.status === 403) {
      toast.error(t('auth.forbidden', 'Forbidden')); // <- Forbidden on classes fetch
    } else {
      toast.error(extractApiMessage(error, 'subjects.classesFetchError'));
    }
    console.error("Error initializing:", error);
  } finally {
    isLoading.value = false;
  }
});
</script>
